/**
 * Native update prompt and mandatory update gate.
 *
 * Renders nothing on web, when current, when no valid store URL is known, or
 * after "Continue for now" this session. The "Update required" screen has no
 * dismiss path; it is deferred by the hook while an appraisal is active.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAppUpdateGate } from "@/hooks/useAppUpdateGate";
import { DEFAULT_UPDATE_MESSAGE } from "@/lib/appUpdate";

export const REQUIRED_UPDATE_MESSAGE =
  "Your version of TransportMate is no longer supported. Please update to continue.";

export function AppUpdateGate() {
  const { policy, visible, blocking, pendingCount, updating, onUpdateNow, onLater } =
    useAppUpdateGate();

  if (!visible || !policy) return null;

  if (blocking) {
    return (
      <AlertDialog open>
        <AlertDialogContent
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="app-update-required"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Update required</AlertDialogTitle>
            <AlertDialogDescription>{REQUIRED_UPDATE_MESSAGE}</AlertDialogDescription>
            {pendingCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {pendingCount} item{pendingCount === 1 ? "" : "s"} still syncing. Keep the app
                installed and update — your saved work will finish uploading afterwards.
              </p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={onUpdateNow} disabled={updating}>
              Update TransportMate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>An update is available</AlertDialogTitle>
          <AlertDialogDescription>
            {policy.updateMessage ?? DEFAULT_UPDATE_MESSAGE}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onLater}>Continue for now</AlertDialogCancel>
          <AlertDialogAction onClick={onUpdateNow} disabled={updating}>
            Update Now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
